import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faEdit, 
  faTrashAlt, 
  faEye, 
  faPlus,
  faFileAlt,
  faFileDownload,
  faChevronDown,
  faChevronUp,
  faLayerGroup,
  faProgrammer,
  faFolder,
  faFolderOpen,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import { ADMIN_URLS } from '../../components/config/apiConfig';

function ResourcesList() {
  const [resources, setResources] = useState([]);
  const [groupedResources, setGroupedResources] = useState({});
  const [programsData, setProgramsData] = useState({});
  const [expandedPrograms, setExpandedPrograms] = useState({});
  const [showIsolated, setShowIsolated] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState({
    category: '',
    accessLevel: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState(null);
  
  useEffect(() => {
    fetchResources();
    fetchPrograms();
  }, [currentPage, filter]);
  
  useEffect(() => {
    // Group resources by program whenever the resources array changes
    groupResourcesByProgram(resources);
  }, [resources]);
  
  const fetchResources = async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        ...(filter.category && { document_type: filter.category }),
        ...(filter.accessLevel && { access_level: filter.accessLevel }),
        ...(searchTerm && { search: searchTerm })
      }).toString();
      
      const response = await fetch(`${ADMIN_URLS.RESOURCES}?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch resources');
      }
      
      const data = await response.json();
      setResources(data?.items || []);
      if (data?.pagination) {
        setCurrentPage(data.pagination.page);
        setTotalPages(data.pagination.total_pages);
      }
    } catch (error) {
      console.error('Error fetching resources:', error);
      setResources([]);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchPrograms = async () => {
    try {
      const response = await fetch(`${ADMIN_URLS.PROGRAMS}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch programs');
      }
      
      const data = await response.json();
      const programsMap = {};
      
      if (data?.items) {
        data.items.forEach(program => {
          programsMap[program.id] = program;
          // Automatically expand programs that might have resources
          setExpandedPrograms(prev => ({
            ...prev,
            [program.id]: true
          }));
        });
      }
      
      setProgramsData(programsMap);
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  };
  
  const groupResourcesByProgram = (resourcesList) => {
    const grouped = {};
    const noProgram = []; // For resources without a program
    
    resourcesList.forEach(resource => {
      if (resource.program_id) {
        if (!grouped[resource.program_id]) {
          grouped[resource.program_id] = [];
        }
        grouped[resource.program_id].push(resource);
      } else {
        noProgram.push(resource);
      }
    });
    
    const result = { ...grouped };
    if (noProgram.length > 0) {
      result['no_program'] = noProgram;
    }
    
    setGroupedResources(result);
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page on search
    fetchResources();
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter({
      ...filter,
      [name]: value
    });
    setCurrentPage(1); // Reset to first page on filter change
  };
  
  const handleDelete = (resource) => {
    setResourceToDelete(resource);
    setShowDeleteModal(true);
  };
  
  const confirmDelete = async () => {
    try {
      const response = await fetch(ADMIN_URLS.RESOURCE_DETAIL(resourceToDelete.id), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete resource');
      }
      
      // Remove resource from state
      setResources(resources.filter(resource => resource.id !== resourceToDelete.id));
      setShowDeleteModal(false);
      setResourceToDelete(null);
    } catch (error) {
      console.error('Error deleting resource:', error);
    }
  };
  
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setResourceToDelete(null);
  };
  
  const toggleProgramExpansion = (programId) => {
    setExpandedPrograms(prev => ({
      ...prev,
      [programId]: !prev[programId]
    }));
  };
  
  const toggleIsolatedResources = () => {
    setShowIsolated(!showIsolated);
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };
  
  const getCategoryLabel = (category) => {
    const categories = {
      'standards': 'Standards & Guidelines',
      'case-studies': 'Case Studies',
      'templates': 'Templates',
      'research': 'Research',
      'presentation': 'Presentations',
      'document': 'Documents'
    };
    
    return categories[category] || category;
  };
  
  const getAccessLevelLabel = (isPublic) => {
    return isPublic ? 'Public' : 'Members Only';
  };
  
  const getFileTypeIcon = (resourceType) => {
    switch (resourceType?.toLowerCase()) {
      case 'document':
        return <FontAwesomeIcon icon={faFileAlt} style={{ color: '#dc3545' }} />;
      case 'presentation':
        return <FontAwesomeIcon icon={faFileAlt} style={{ color: '#fd7e14' }} />;
      default:
        return <FontAwesomeIcon icon={faFileAlt} style={{ color: '#6c757d' }} />;
    }
  };

  const renderResourcesTable = (resourcesArray, programId) => {
    return (
      <table className="admin-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Type</th>
            <th>URL</th>
            <th>Access</th>
            <th>Created Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {resourcesArray?.length > 0 ? (
            resourcesArray.map((resource) => (
              <tr key={resource.id}>
                <td>{resource.id}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {getFileTypeIcon(resource.resource_type)}
                    <span>{resource.resource_type}</span>
                  </div>
                </td>
                <td>
                  <div className="url-cell">
                    {resource.url ? 
                      (resource.url.length > 40 ? 
                        `${resource.url.substring(0, 40)}...` : 
                        resource.url) : 
                      'No URL'}
                  </div>
                </td>
                <td>{getAccessLevelLabel(resource.is_public)}</td>
                <td>{formatDate(resource.created_at)}</td>
                <td>
                  <div className="admin-table-actions">
                    <Link 
                      to={`/admin/resources/${resource.id}`} 
                      className="admin-table-btn admin-table-btn-view"
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </Link>
                    <Link 
                      to={`/admin/resources/${resource.id}/edit`} 
                      className="admin-table-btn admin-table-btn-edit"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </Link>
                    <button 
                      className="admin-table-btn admin-table-btn-delete"
                      onClick={() => handleDelete(resource)}
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </button>
                    {resource.url && resource.url.startsWith('/uploads') && (
                      <a 
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="admin-table-btn"
                        style={{ backgroundColor: '#28a745', color: 'white' }}
                      >
                        <FontAwesomeIcon icon={faFileDownload} />
                      </a>
                    )}
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>
                No resources found for this program
              </td>
            </tr>
          )}
        </tbody>
      </table>
    );
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Resources</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard" className="admin-breadcrumb-link">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item active">
            <span className="admin-breadcrumb-link">Resources</span>
          </li>
        </ul>
      </div>
      
      <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
        <div className="admin-card-body" style={{ padding: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ position: 'relative', marginRight: '1rem' }}>
                <input 
                  type="text" 
                  placeholder="Search resources..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ 
                    padding: '0.5rem 0.5rem 0.5rem 2rem', 
                    borderRadius: '4px', 
                    border: '1px solid var(--admin-border)',
                    minWidth: '250px'
                  }}
                />
                <FontAwesomeIcon 
                  icon={faSearch} 
                  style={{ 
                    position: 'absolute', 
                    left: '0.5rem', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    color: '#6c757d'
                  }}
                />
              </div>
              <button 
                type="submit"
                className="admin-btn admin-btn-primary"
                style={{ padding: '0.5rem 1rem' }}
              >
                Search
              </button>
            </form>
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <select
                name="accessLevel"
                value={filter.accessLevel}
                onChange={handleFilterChange}
                style={{ 
                  padding: '0.5rem', 
                  borderRadius: '4px', 
                  border: '1px solid var(--admin-border)'
                }}
              >
                <option value="">All Access Levels</option>
                <option value="public">Public</option>
                <option value="private">Members Only</option>
              </select>
              
              <Link to="/admin/resources/add" className="admin-btn admin-btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem' }}>
                <FontAwesomeIcon icon={faPlus} />
                Add Resource
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      <div className="admin-resources-container">
        {isLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>Loading resources...</div>
        ) : (
          <>
            {/* Program-based resources */}
            {Object.keys(groupedResources).length === 0 ? (
              <div className="admin-card admin-empty-state">
                <FontAwesomeIcon icon={faExclamationCircle} size="3x" />
                <h3>No Resources Found</h3>
                <p>There are no resources that match your search criteria.</p>
                <Link to="/admin/resources/add" className="admin-btn admin-btn-primary">
                  Add Your First Resource
                </Link>
              </div>
            ) : (
              <>
                {Object.keys(groupedResources).map(programId => {
                  if (programId === 'no_program' && !showIsolated) return null;
                  
                  const isProgramGroup = programId !== 'no_program';
                  const program = isProgramGroup ? programsData[programId] : null;
                  const isExpanded = expandedPrograms[programId];
                  
                  return (
                    <div key={programId} className="admin-resource-group">
                      <div 
                        className={`admin-resource-group-header ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => toggleProgramExpansion(programId)}
                      >
                        <div className="admin-resource-group-title">
                          <FontAwesomeIcon 
                            icon={isProgramGroup ? (isExpanded ? faFolderOpen : faFolder) : faLayerGroup} 
                            className="admin-resource-group-icon"
                          />
                          <span>{isProgramGroup ? (program?.title || `Program ${programId}`) : 'Resources Without Program'}</span>
                          <span className="admin-resource-count">{groupedResources[programId].length}</span>
                        </div>
                        <FontAwesomeIcon 
                          icon={isExpanded ? faChevronUp : faChevronDown} 
                          className="admin-resource-chevron"
                        />
                      </div>
                      
                      {isExpanded && (
                        <div className="admin-resource-group-content">
                          {renderResourcesTable(groupedResources[programId], programId)}
                        </div>
                      )}
                    </div>
                  );
                })}
              
                {/* Toggle option for resources without programs */}
                {groupedResources['no_program'] && (
                  <div className="admin-resources-toggle">
                    <button 
                      className="admin-resources-toggle-btn"
                      onClick={toggleIsolatedResources}
                    >
                      {showIsolated ? 'Hide' : 'Show'} resources without a program
                    </button>
                  </div>
                )}
              </>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="admin-pagination">
                <div className="admin-pagination-controls">
                  <button 
                    className="admin-btn"
                    style={{ 
                      backgroundColor: '#f8f9fa', 
                      color: 'var(--admin-dark)',
                      padding: '0.5rem 0.75rem'
                    }}
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(page => Math.max(page - 1, 1))}
                  >
                    &laquo;
                  </button>
                  
                  {[...Array(totalPages)].map((_, i) => (
                    <button 
                      key={i}
                      className="admin-btn"
                      style={{ 
                        backgroundColor: currentPage === i + 1 ? 'var(--admin-primary)' : '#f8f9fa',
                        color: currentPage === i + 1 ? 'white' : 'var(--admin-dark)',
                        padding: '0.5rem 0.75rem'
                      }}
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  
                  <button 
                    className="admin-btn"
                    style={{ 
                      backgroundColor: '#f8f9fa',
                      color: 'var(--admin-dark)',
                      padding: '0.5rem 0.75rem'
                    }}
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(page => Math.min(page + 1, totalPages))}
                  >
                    &raquo;
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      
      {showDeleteModal && resourceToDelete && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <h2>Confirm Deletion</h2>
            <p>
              Are you sure you want to delete the resource? This action cannot be undone.
            </p>
            <div className="admin-modal-actions">
              <button 
                className="admin-btn admin-btn-light"
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button 
                className="admin-btn admin-btn-danger"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        .admin-resources-container {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 2rem;
        }
        
        .admin-resource-group {
          border: 1px solid var(--admin-border);
          border-radius: 8px;
          overflow: hidden;
          background-color: white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }
        
        .admin-resource-group-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          background-color: #f8f9fa;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }
        
        .admin-resource-group-header:hover {
          background-color: #e9ecef;
        }
        
        .admin-resource-group-header.expanded {
          background-color: #e9ecef;
          border-bottom: 1px solid var(--admin-border);
        }
        
        .admin-resource-group-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-weight: 500;
          font-size: 1.1rem;
          color: #495057;
        }
        
        .admin-resource-group-icon {
          color: #6c757d;
        }
        
        .admin-resource-count {
          font-size: 0.9rem;
          background-color: #e2e3e5;
          color: #495057;
          padding: 0.2rem 0.5rem;
          border-radius: 20px;
        }
        
        .admin-resource-chevron {
          color: #6c757d;
          transition: transform 0.2s ease;
        }
        
        .admin-resource-group-header.expanded .admin-resource-chevron {
          transform: rotate(180deg);
        }
        
        .admin-resource-group-content {
          padding: 0;
        }
        
        .admin-resources-toggle {
          display: flex;
          justify-content: center;
          margin-top: 1rem;
        }
        
        .admin-resources-toggle-btn {
          background: none;
          border: none;
          color: var(--admin-primary);
          cursor: pointer;
          padding: 0.5rem 1rem;
          font-size: 0.9rem;
          text-decoration: underline;
        }
        
        .admin-resources-toggle-btn:hover {
          color: var(--admin-primary-dark);
        }
        
        .admin-pagination {
          display: flex;
          justify-content: center;
          margin-top: 2rem;
        }
        
        .admin-pagination-controls {
          display: flex;
          gap: 0.25rem;
        }
        
        .admin-empty-state {
          padding: 3rem;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          color: #6c757d;
        }
        
        .admin-empty-state h3 {
          color: #343a40;
          margin: 0.5rem 0;
        }
        
        .admin-empty-state p {
          margin-bottom: 1.5rem;
        }
        
        .admin-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1050;
        }
        
        .admin-modal {
          background-color: white;
          border-radius: 8px;
          padding: 2rem;
          width: 100%;
          max-width: 400px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        
        .admin-modal h2 {
          margin-bottom: 1rem;
        }
        
        .admin-modal p {
          margin-bottom: 1.5rem;
        }
        
        .admin-modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
        }
        
        .url-cell {
          max-width: 300px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      `}</style>
    </AdminLayout>
  );
}

export default ResourcesList;
